import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PackageCheck, CheckCircle2, XCircle, Loader2, Truck, MapPin, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { InclusionsField } from "@/components/InclusionsField";
import { toast } from "sonner";

export default function BidPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [price, setPrice] = useState("");
  const [inclusions, setInclusions] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = () =>
    api.get(`/bid/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.multi) {
          setRows((r.data.consignments || []).map((c) => ({ consignment_idx: c.consignment_idx, label: c.label, description: c.description, quoted_price: c.quoted_price != null ? String(c.quoted_price) : "", inclusions: c.inclusions || "" })));
        } else if (r.data.quoted_price != null) {
          setPrice(String(r.data.quoted_price));
        }
      })
      .catch((e) => setErr(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (data.multi) {
        const quotes = rows.filter((r) => r.quoted_price !== "" && Number(r.quoted_price) > 0).map((r) => ({ consignment_idx: r.consignment_idx, quoted_price: parseFloat(r.quoted_price), inclusions: r.inclusions }));
        if (!quotes.length) { toast.error("Enter a rate for at least one consignment"); setBusy(false); return; }
        await api.post(`/bid/${token}`, { quotes });
      } else {
        await api.post(`/bid/${token}`, { quoted_price: parseFloat(price), inclusions });
      }
      setSubmitted(true);
      toast.success("Bid submitted — thank you!");
      load();
    } catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  if (err) return <Center><div className="text-center"><XCircle className="h-10 w-10 text-destructive mx-auto mb-3" /><p className="text-muted-foreground">{err}</p></div></Center>;
  if (!data) return <Center><Loader2 className="h-8 w-8 animate-spin text-primary" /></Center>;

  const sl = data.service_lines || {};
  const services = ["loading", "unloading", "packaging"].filter((k) => sl[k]);

  return (
    <div className="min-h-screen bg-muted py-10 px-4">
      <div className="max-w-xl mx-auto bg-card border border-border" data-testid="bid-portal">
        <div className="flex items-center justify-between p-6 border-b-4 border-primary">
          <Logo className="h-11" />
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">RFQ</div>
            <div className="font-mono-num font-bold text-primary" data-testid="bid-order-number">{data.order_number}</div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="text-sm">Hello <span className="font-bold">{data.vendor_name}</span>, please submit your best quote for the shipment below. Customer identity is withheld.</div>

          <div className="space-y-2" data-testid="bid-route">
            <RouteBlock icon={ArrowUpRight} label="Pickup (From)" p={data.pickup} />
            <RouteBlock icon={ArrowDownRight} label="Drop (To)" p={data.delivery} />
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Box label="Category" value={<span className="capitalize">{data.category}</span>} />
            <Box label="Move date" value={data.move_date || "Flexible"} />
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Items</div>
            <div className="border border-border divide-y divide-border">
              {(data.items || []).map((it, i) => <div key={i} className="flex justify-between p-2 px-3 text-sm"><span>{it.name}</span><span className="font-mono-num text-muted-foreground">×{it.quantity}</span></div>)}
              {(!data.items || !data.items.length) && <div className="p-3 text-sm text-muted-foreground">No items listed</div>}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Services required</div>
            {services.length ? (
              <div className="flex flex-wrap gap-2">{services.map((s) => <span key={s} className="capitalize text-xs border border-border px-3 py-1.5 bg-muted">{s}</span>)}</div>
            ) : <div className="text-sm text-muted-foreground">Transport only</div>}
          </div>

          {data.locked ? (
            <div className="border border-border bg-muted p-4 text-center text-sm text-muted-foreground"><Truck className="h-6 w-6 mx-auto mb-2 text-primary" />This order has been confirmed. Bidding is closed.</div>
          ) : submitted ? (
            <div className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 p-5 text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
              <div className="font-bold">Bid submitted</div>
              <div className="text-sm opacity-80">Your quote of {money(price)} is recorded. You may update it below any time before the order is confirmed.</div>
            </div>
          ) : null}

          {!data.locked && (
            <form onSubmit={submit} className="space-y-4 pt-2">
              {data.multi ? (
                <div className="space-y-4" data-testid="bid-consignments">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Quote each consignment</div>
                  {rows.map((r, i) => (
                    <div key={r.consignment_idx} className="border border-border p-3 space-y-3" data-testid={`bid-consignment-${r.consignment_idx}`}>
                      <div className="text-sm font-bold">RFQ {r.consignment_idx + 1} · {r.label}{r.description ? <span className="font-normal text-muted-foreground"> — {r.description}</span> : ""}</div>
                      <div className="space-y-1.5">
                        <Label>Your quoted price (₹)</Label>
                        <Input type="number" min="1" step="0.01" className="rounded-none font-mono-num text-lg h-12" value={r.quoted_price} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, quoted_price: e.target.value } : x))} placeholder="e.g. 16500" data-testid={`bid-price-${r.consignment_idx}`} />
                      </div>
                      <InclusionsField value={r.inclusions} onChange={(v) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, inclusions: v } : x))} testid={`bid-inclusions-${r.consignment_idx}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="bid">Your quoted price (₹)</Label>
                    <Input id="bid" type="number" min="1" step="0.01" className="rounded-none font-mono-num text-lg h-12" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 16500" required data-testid="bid-price-input" />
                  </div>
                  <InclusionsField value={inclusions} onChange={setInclusions} testid="bid-inclusions" />
                </>
              )}
              <Button type="submit" disabled={busy} className="w-full h-12 rounded-none font-bold uppercase tracking-wide" data-testid="bid-submit">
                {busy ? "Submitting…" : "Submit bid"}
              </Button>
            </form>
          )}
        </div>
        <div className="px-6 py-4 border-t border-border text-center text-xs text-muted-foreground">Arrow Logistics · Secure partner bidding portal</div>
      </div>
    </div>
  );
}

const Box = ({ label, value }) => (<div className="border border-border p-3"><div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="font-medium mt-0.5">{value}</div></div>);
const RouteBlock = ({ icon: Icon, label, p = {} }) => (
  <div className="border border-border p-3">
    <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-1">{Icon ? <Icon className="h-3 w-3 text-primary" /> : <MapPin className="h-3 w-3 text-primary" />}{label}</div>
    <div className="font-medium text-sm">{p.full || "—"}</div>
    <div className="text-xs text-muted-foreground mt-1">Floor Level: <span className="text-foreground">{p.floor || "—"}</span></div>
    <div className="text-xs text-muted-foreground">{p.lift_text || "Lift Not Available"}</div>
  </div>
);
const Center = ({ children }) => <div className="min-h-screen grid place-items-center bg-background">{children}</div>;
